import { useContext, useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { quoteLineType, quoteType } from '../types'
import { dataContext } from '..'
import axios from 'axios'
import { DropResult } from '@hello-pangea/dnd'

export type DrawerState = {
  type: 'edit' | 'new' | 'menu' | 'template-menu' | 'version-menu' | null
  lineItem?: quoteLineType
  lineNumber?: number
  bundle?: string | null
  anchorEl?: HTMLElement | null
}

export type SnackbarState = 'success' | 'fail' | null

export interface LinesInterface {
  // State
  quote: quoteType
  lineItems: (quoteLineType & { children: quoteLineType[] })[]
  setLineItems: (items: (quoteLineType & { children: quoteLineType[] })[]) => void
  lineItemsLoading: boolean
  setLineItemsLoading: (loading: boolean) => void
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rows: number) => void
  selectedLineItems: quoteLineType[]
  setSelectedLineItems: (items: quoteLineType[] | ((prev: quoteLineType[]) => quoteLineType[])) => void
  drawerState: DrawerState
  setDrawerState: (state: DrawerState) => void
  cloneSnackbar: SnackbarState
  setCloneSnackbar: (state: SnackbarState) => void
  deleteSnackbar: SnackbarState
  setDeleteSnackbar: (state: SnackbarState) => void
  bulkEditSnackbar: SnackbarState
  setBulkEditSnackbar: (state: SnackbarState) => void
  bulkDeleteSnackbar: SnackbarState
  setBulkDeleteSnackbar: (state: SnackbarState) => void
  addLineSnackbar: SnackbarState
  setAddLineSnackbar: (state: SnackbarState) => void
  canDrag: boolean
  setCanDrag: (canDrag: boolean) => void
  bundleSnackbar: SnackbarState
  setBundleSnackbar: (state: SnackbarState) => void
  unbundleSnackbar: SnackbarState
  setUnbundleSnackbar: (state: SnackbarState) => void
  collapsedBundles: Set<string>
  setCollapsedBundles: (bundles: Set<string> | ((prev: Set<string>) => Set<string>)) => void
  lineCount: number
  quoteId: string | undefined
  versions: { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]
  setVersions: (versions: { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]) => void
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  differences: { changes: quoteLineType[]; additions: quoteLineType[]; deletions: quoteLineType[] } | null
  setDifferences: (
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    differences: { changes: quoteLineType[]; additions: quoteLineType[]; deletions: quoteLineType[] } | null,
  ) => void
  // Computed values
  sortedLineItems: (quoteLineType & { children: quoteLineType[] })[]
  // Handlers
  handleReloadLineItems: () => Promise<void>
  handleMenuAction: (action: 'edit' | 'new' | 'clone' | 'delete' | 'unbundle') => void
  handleClone: (lineItem: quoteLineType | null) => void
  handleDelete: (lineItem: quoteLineType | null) => void
  handleUnbundle: (lineItem: quoteLineType | null) => void
  handleBulkEdit: (
    lineItems: (quoteLineType & { children: quoteLineType[] })[],
    previousLineItems: (quoteLineType & { children: quoteLineType[] })[],
  ) => Promise<void>
  handleDrag: (
    result: import('@hello-pangea/dnd').DropResult,
    previousLineItems: (quoteLineType & { children: quoteLineType[] })[],
    setLineItemsLoading: (loading: boolean) => void,
  ) => void
}

export function Lines(): LinesInterface {
  const { quoteId } = useParams()
  const { user } = useContext(dataContext)
  const [quote, setQuote] = useState<quoteType>({} as quoteType)
  const [lineItems, setLineItems] = useState<(quoteLineType & { children: quoteLineType[] })[]>([])
  const [lineItemsLoading, setLineItemsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedLineItems, setSelectedLineItems] = useState<quoteLineType[]>([])
  const [drawerState, setDrawerState] = useState<DrawerState>({ type: null })
  const [cloneSnackbar, setCloneSnackbar] = useState<SnackbarState>(null)
  const [deleteSnackbar, setDeleteSnackbar] = useState<SnackbarState>(null)
  const [bulkEditSnackbar, setBulkEditSnackbar] = useState<SnackbarState>(null)
  const [bulkDeleteSnackbar, setBulkDeleteSnackbar] = useState<SnackbarState>(null)
  const [addLineSnackbar, setAddLineSnackbar] = useState<SnackbarState>(null)
  const [canDrag, setCanDrag] = useState(false)
  const [bundleSnackbar, setBundleSnackbar] = useState<SnackbarState>(null)
  const [unbundleSnackbar, setUnbundleSnackbar] = useState<SnackbarState>(null)
  const [collapsedBundles, setCollapsedBundles] = useState<Set<string>>(new Set())
  const [lineCount, setLineCount] = useState(0)
  const [versions, setVersions] = useState<
    { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]
  >([])
  const [differences, setDifferences] = useState<{
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    changes: quoteLineType[]
    additions: quoteLineType[]
    deletions: quoteLineType[]
  } | null>(null)
  const handleReloadLineItems = async () => {
    if (!user.email || !quoteId) {
      return
    }
    setLineItemsLoading(true)
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        setQuote(res.data.quote)
        setLineItems(res.data.lineItems)
        setLineCount(
          (res.data.lineItems as (quoteLineType & { children: quoteLineType[] })[])
            .flatMap(bundle => bundle.children)
            .filter(item => item.LineNumber !== -1).length +
            (res.data.lineItems as (quoteLineType & { children: quoteLineType[] })[]).filter(
              item => item.LineNumber !== -1,
            ).length,
        )
        setVersions(res.data.versions)
        setDifferences(res.data.differences)
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        setLineItemsLoading(false)
      })
  }

  useEffect(() => {
    handleReloadLineItems()
  }, [user.email, quoteId])

  const sortedLineItems = useMemo(() => {
    // Flatten all actual line items (excluding bundle headers) with their bundle info
    type FlatItem = {
      item: quoteLineType
      bundleHeader?: quoteLineType & { children: quoteLineType[] }
      bundleId: string | null
    }
    const flatItems: FlatItem[] = lineItems.flatMap(bundle =>
      bundle.LineNumber === -1
        ? bundle.children.map<FlatItem>(child => ({
            item: child,
            bundleHeader: bundle,
            bundleId: bundle.Bundle || null,
          }))
        : ([{ item: bundle, bundleId: null }] as FlatItem[]),
    )

    // Get the items for the current page (only counting actual line items)
    const pageItems = flatItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    // Group bundle items, then build result preserving order
    const bundleGroups = new Map<
      string,
      { header: quoteLineType & { children: quoteLineType[] }; items: quoteLineType[] }
    >()
    const result: (quoteLineType & { children: quoteLineType[] })[] = []
    const seenBundles = new Set<string>()

    for (const { item, bundleHeader, bundleId } of pageItems) {
      if (bundleId && bundleHeader) {
        if (!bundleGroups.has(bundleId)) {
          bundleGroups.set(bundleId, { header: bundleHeader, items: [] })
        }
        bundleGroups.get(bundleId)?.items.push(item)
      }
    }

    // Build result in order, adding bundles when first encountered
    for (const { item, bundleHeader, bundleId } of pageItems) {
      if (bundleId && bundleHeader) {
        if (!seenBundles.has(bundleId)) {
          seenBundles.add(bundleId)
          result.push({ ...bundleHeader, children: bundleGroups.get(bundleId)?.items ?? [] })
        }
      } else {
        result.push({ ...item, children: [] })
      }
    }

    return result
  }, [lineItems, page, rowsPerPage])

  const handleMenuAction = (action: 'edit' | 'new' | 'clone' | 'delete' | 'unbundle') => {
    const { lineItem } = drawerState
    switch (action) {
      case 'edit':
        setDrawerState({ type: 'edit', lineItem })
        break
      case 'new':
        if (lineItem?.LineNumber === -1) {
          setDrawerState({
            type: 'new',
            lineNumber: Math.max(
              ...(lineItems.find(bundle => bundle.Bundle === lineItem.Bundle)?.children.map(item => item.LineNumber) ??
                []),
            ),
            bundle: lineItem.Bundle,
          })
        } else {
          setDrawerState({ type: 'new', lineNumber: lineItem?.LineNumber, bundle: lineItem?.Bundle })
        }
        break
      case 'clone':
        handleClone(lineItem ?? null)
        setDrawerState({ type: null })
        break
      case 'unbundle':
        handleUnbundle(lineItem ?? null)
        setDrawerState({ type: null })
        break
      case 'delete':
        handleDelete(lineItem ?? null)
        setDrawerState({ type: null })
        break
    }
  }

  const handleClone = (lineItem: quoteLineType | null) => {
    if (!lineItem) return

    if (lineItem.LineNumber === -1) {
      const bundleToClone = lineItems.find(item => item.LineItemId === lineItem.LineItemId)
      if (!bundleToClone) return

      const allLineNumbers = lineItems
        .flatMap(bundle =>
          bundle.LineNumber === -1 ? bundle.children.map(child => child.LineNumber) : [bundle.LineNumber],
        )
        .filter(num => num !== -1)
      const maxLineNumber = allLineNumbers.length > 0 ? Math.max(...allLineNumbers) : 0

      const newBundleId = crypto.randomUUID()
      const clonedHeader: quoteLineType = {
        ...bundleToClone,
        LineItemId: crypto.randomUUID(),
        Bundle: newBundleId,
      }
      let nextLineNumber = maxLineNumber + 1
      const clonedChildren: quoteLineType[] = bundleToClone.children.map(child => ({
        ...child,
        LineItemId: crypto.randomUUID(),
        Bundle: newBundleId,
        LineNumber: nextLineNumber++,
      }))
      const newBundle: quoteLineType & { children: quoteLineType[] } = {
        ...clonedHeader,
        children: clonedChildren,
      }

      const bundleIndex = lineItems.findIndex(item => item.LineItemId === lineItem.LineItemId)
      const insertIndex = bundleIndex >= 0 ? bundleIndex + 1 : lineItems.length

      const updatedLineItems = [
        ...lineItems.slice(0, insertIndex),
        newBundle,
        ...lineItems.slice(insertIndex),
      ] as (quoteLineType & { children: quoteLineType[] })[]

      handleBulkEdit(updatedLineItems, lineItems).catch(err => {
        console.error(err)
      })
    } else {
      axios
        .post(`/cloneLineItem/${lineItem.LineItemId}`, { quoteId: quoteId, lineNumber: lineItem.LineNumber })
        .then(res => {
          setLineItems(res.data)
          setCloneSnackbar('success')
          const lineItemToEdit = lineItem.Bundle
            ? res.data
                .find((bundle: quoteLineType & { children: quoteLineType[] }) => bundle.Bundle === lineItem.Bundle)
                ?.children.find((item: quoteLineType) => item.LineNumber === lineItem.LineNumber + 1)
            : res.data.find((item: quoteLineType) => item.LineNumber === lineItem.LineNumber + 1)
          setDrawerState({ type: 'edit', lineItem: lineItemToEdit })
        })
        .catch(err => {
          console.error(err)
          setCloneSnackbar('fail')
        })
    }
  }

  const handleDelete = (lineItem: quoteLineType | null) => {
    if (!lineItem) return
    axios
      .post(`/deleteLineItem/${lineItem.LineItemId}`, {
        quoteId: quoteId,
        lineNumber: lineItem.LineNumber,
        bundleId: lineItem.Bundle,
      })
      .then(res => {
        setLineItems(res.data)
        setDeleteSnackbar('success')
      })
      .catch(err => {
        console.error(err)
        setDeleteSnackbar('fail')
      })
  }

  const handleUnbundle = (lineItem: quoteLineType | null) => {
    if (!lineItem || !lineItem.Bundle) return

    const flatLineItems: quoteLineType[] = lineItems.flatMap(bundle => {
      if (bundle.LineNumber === -1) {
        return [bundle, ...bundle.children]
      } else {
        return [bundle]
      }
    })

    if (lineItem.LineNumber === -1 || flatLineItems.filter(item => item.Bundle === lineItem.Bundle).length === 2) {
      const updatedLineItems: quoteLineType[] = flatLineItems
        .filter(item => item.Bundle === lineItem.Bundle && item.LineNumber !== -1)
        .map(item => ({ ...item, Bundle: null }))
      updatedLineItems.push(...flatLineItems.filter(item => item.Bundle !== lineItem.Bundle))
      axios
        .post(`/bulkEditLineItems/${quoteId}`, { lineItems: updatedLineItems })
        .then(res => {
          setLineItems(res.data)
          setUnbundleSnackbar('success')
        })
        .catch(err => {
          console.error(err)
          setUnbundleSnackbar('fail')
        })
    } else {
      // Unbundle single item from bundle
      const updatedLineItems: quoteLineType[] = flatLineItems
        .filter(item => item.LineItemId === lineItem.LineItemId)
        .map(item => ({ ...item, Bundle: null }))
      updatedLineItems.push(...flatLineItems.filter(item => item.LineItemId !== lineItem.LineItemId))
      axios
        .post(`/bulkEditLineItems/${quoteId}`, { lineItems: updatedLineItems })
        .then(res => {
          setLineItems(res.data)
          setUnbundleSnackbar('success')
        })
        .catch(err => {
          console.error(err)
          setUnbundleSnackbar('fail')
        })
    }
  }

  const handleBulkEdit = async (
    lineItems: (quoteLineType & { children: quoteLineType[] })[],
    previousLineItems: (quoteLineType & { children: quoteLineType[] })[],
  ) => {
    return new Promise<void>((resolve, reject) => {
      const flatLineItems: quoteLineType[] = lineItems.flatMap(bundle => {
        if (bundle.LineNumber === -1) {
          return [bundle, ...bundle.children]
        } else {
          return [bundle]
        }
      })
      axios
        .post(`/bulkEditLineItems/${quoteId}`, { lineItems: flatLineItems })
        .then(res => {
          setLineItems(res.data)
          setBulkEditSnackbar('success')
          resolve()
        })
        .catch(err => {
          console.error(err)
          setLineItems(previousLineItems)
          setBulkEditSnackbar('fail')
          reject(err)
        })
    })
  }

  const handleDrag = (
    result: DropResult,
    previousLineItems: (quoteLineType & { children: quoteLineType[] })[],
    setLineItemsLoading: (loading: boolean) => void,
  ) => {
    setLineItemsLoading(true)
    if (!result.destination || result.source.index === result.destination.index) {
      return
    }

    type FlatItem = { item: quoteLineType; bundleId: string | null; isHeader: boolean }
    const flatItems: FlatItem[] = lineItems.flatMap(bundle => {
      const bundleId = bundle.Bundle || null
      const isHeader = bundle.LineNumber === -1
      return isHeader
        ? [
            { item: bundle, bundleId, isHeader: true },
            ...bundle.children.map(lineItem => ({ item: lineItem, bundleId, isHeader: false })),
          ]
        : [{ item: bundle, bundleId: null, isHeader: false }]
    })

    const { index: sourceIndex } = result.source
    const { index: destIndex } = result.destination
    const sourceItem = flatItems[sourceIndex]

    if (sourceItem.isHeader) {
      // Move entire bundle
      const bundleLength = flatItems.filter(f => f.bundleId === sourceItem.bundleId).length
      const bundleStartIndex = flatItems.findIndex(f => f.bundleId === sourceItem.bundleId && f.isHeader)
      const itemsToMove = flatItems.splice(bundleStartIndex, bundleLength)
      // destination.index is relative to the original array
      // After removing bundle, if dragging down, adjust for removed items
      const insertIndex = bundleStartIndex < destIndex ? destIndex - bundleLength : destIndex
      flatItems.splice(insertIndex, 0, ...itemsToMove)
    } else if (sourceItem.bundleId) {
      // Move item within bundle - check boundaries
      const bundleIndices = flatItems.map((f, i) => (f.bundleId === sourceItem.bundleId ? i : -1)).filter(i => i !== -1)
      const [bundleStart, bundleEnd] = [Math.min(...bundleIndices), Math.max(...bundleIndices)]

      if (destIndex < bundleStart || destIndex > bundleEnd) return

      const [movedItem] = flatItems.splice(sourceIndex, 1)
      // destination.index is relative to the original array
      // After removing item, if dragging down, the destination index shifts by 1
      // But the library's destination.index already accounts for where the item should go
      // So we use destIndex directly (the library handles the adjustment)
      flatItems.splice(destIndex, 0, movedItem)
    } else {
      // Move standalone item
      const [movedItem] = flatItems.splice(sourceIndex, 1)
      // destination.index is relative to the original array
      // The library provides the index where the item should be placed after removal
      // So we use destIndex directly
      flatItems.splice(destIndex, 0, movedItem)
    }

    let lineNumber = 1
    const updatedBundledItems: (quoteLineType & { children: quoteLineType[] })[] = []

    for (let i = 0; i < flatItems.length; i++) {
      const current = flatItems[i]

      if (current.isHeader) {
        const bundleId = current.bundleId
        const children: quoteLineType[] = []

        while (++i < flatItems.length && flatItems[i].bundleId === bundleId && !flatItems[i].isHeader) {
          children.push({ ...flatItems[i].item, LineNumber: lineNumber++, Bundle: bundleId })
        }
        i--

        updatedBundledItems.push({ ...current.item, LineNumber: -1, children })
      } else {
        updatedBundledItems.push({ ...current.item, LineNumber: lineNumber++, Bundle: null, children: [] })
      }
    }

    handleBulkEdit(updatedBundledItems, previousLineItems).then(() => {
      setLineItemsLoading(false)
    })
  }

  return {
    // State
    quote,
    lineItems,
    setLineItems,
    lineItemsLoading,
    setLineItemsLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    selectedLineItems,
    setSelectedLineItems,
    drawerState,
    setDrawerState,
    cloneSnackbar,
    setCloneSnackbar,
    deleteSnackbar,
    setDeleteSnackbar,
    bulkEditSnackbar,
    setBulkEditSnackbar,
    bulkDeleteSnackbar,
    setBulkDeleteSnackbar,
    addLineSnackbar,
    setAddLineSnackbar,
    canDrag,
    setCanDrag,
    bundleSnackbar,
    setBundleSnackbar,
    unbundleSnackbar,
    setUnbundleSnackbar,
    collapsedBundles,
    setCollapsedBundles,
    lineCount,
    quoteId,
    versions,
    setVersions,
    differences,
    setDifferences,
    // Computed values
    sortedLineItems,
    // Handlers
    handleReloadLineItems,
    handleMenuAction,
    handleClone,
    handleDelete,
    handleUnbundle,
    handleBulkEdit,
    handleDrag,
  }
}
